import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { contactHelp } from "@/modules/core/help/contact-help";
import { DeskGlancePeekShell } from "@/modules/shell/DeskGlancePeekShell";
import { DetailReadout } from "@/modules/shell/DetailReadout";
import { stashListReturn } from "@/modules/shell/list-return-state";
import { getParty } from "@/shared/api/parties";
import { notify } from "@/shared/feedback/notify";
import { queryKeys } from "@/shared/query/keys";
import { contactGlanceStats } from "../contact-glance-math";

/**
 * Desk side glance — party facts · View more (plan/45).
 * No Edit. Prefetches full detail so View more feels instant.
 */
export function ContactGlancePeek(props: {
  open: boolean;
  partyId: string | null;
  companySlug: string;
  listPath: string;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const partyId = props.partyId;

  const detailQuery = useQuery({
    queryKey: queryKeys.parties.detail(props.companySlug, partyId ?? ""),
    enabled: props.open && Boolean(partyId),
    queryFn: () => getParty(props.companySlug, partyId!),
  });

  useEffect(() => {
    if (!props.open || !partyId) return;
    void queryClient.prefetchQuery({
      queryKey: queryKeys.parties.detail(props.companySlug, partyId),
      queryFn: () => getParty(props.companySlug, partyId),
    });
  }, [props.open, partyId, props.companySlug, queryClient]);

  const party = detailQuery.data?.party ?? null;
  const stats = party ? contactGlanceStats(party) : null;
  const title = party?.name ?? "Contact";

  function viewMore() {
    if (!partyId) return;
    stashListReturn(props.listPath, partyId);
    props.onOpenChange(false);
    navigate(`/c/${props.companySlug}/contacts/${partyId}`);
  }

  async function copyPhone() {
    const phone = stats?.primaryPhone?.trim();
    if (!phone) return;
    try {
      await navigator.clipboard.writeText(phone);
      notify.info(`Copied ${phone}`);
    } catch {
      notify.error("Could not copy phone");
    }
  }

  return (
    <DeskGlancePeekShell
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={title}
    >
      {detailQuery.isPending && !party ? (
        <Alert variant="muted">Loading…</Alert>
      ) : detailQuery.isError ? (
        <Alert variant="destructive">
          {detailQuery.error instanceof Error
            ? detailQuery.error.message
            : "Failed to load"}
        </Alert>
      ) : party && stats ? (
        <>
          <DetailReadout
            className="mt-0"
            rows={[
              { label: "Name", value: party.name },
              ...(stats.code
                ? [{ label: "Code", value: stats.code, mono: true as const }]
                : []),
              {
                label: "Roles",
                value: stats.roleLabel,
                help: contactHelp.roles,
              },
              ...(stats.deactivated
                ? [{ label: "Status", value: "Deactivated" }]
                : []),
              {
                label: "Phone",
                value: stats.primaryPhone ?? "—",
                mono: Boolean(stats.primaryPhone),
              },
              {
                label: "Email",
                value: stats.primaryEmail ?? "—",
              },
              ...(stats.outstandingAr
                ? [
                    {
                      label: "AR open",
                      value: `₱${stats.outstandingAr}`,
                      mono: true as const,
                      help: contactHelp.arOpen,
                    },
                  ]
                : []),
              ...(stats.outstandingAp
                ? [
                    {
                      label: "AP open",
                      value: `₱${stats.outstandingAp}`,
                      mono: true as const,
                      help: contactHelp.apOpen,
                    },
                  ]
                : []),
              ...((party.partyKind ?? "org") === "org"
                ? [
                    {
                      label: "People",
                      value:
                        stats.peopleCount === 0
                          ? "None linked"
                          : `${stats.peopleCount} linked`,
                      help: contactHelp.people,
                    },
                  ]
                : []),
              ...(stats.notesPreview
                ? [{ label: "Notes", value: stats.notesPreview }]
                : []),
            ]}
          />
          <div className="mt-6 flex flex-col gap-2">
            <Button type="button" onClick={() => viewMore()}>
              View more
            </Button>
            {stats.primaryPhone ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => void copyPhone()}
              >
                Copy phone
              </Button>
            ) : null}
            {party.isCustomer || party.isSupplier ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (!partyId) return;
                  props.onOpenChange(false);
                  navigate(
                    `/c/${props.companySlug}/contacts/${partyId}/statement`,
                  );
                }}
              >
                Print statement
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </DeskGlancePeekShell>
  );
}
